import { Schedule } from './schedule';
import { Status, StatusEffect } from './status';

export type DiaryEntry = {
    time: number;
    state: Status;
    appliedEffects: StatusEffect[];
    tickEffects: StatusEffect[];
}

export type DiaryPage = {
    day: string;
    entries: DiaryEntry[];
};

export type Diary = DiaryPage[];

export type GrandmaTickResult = {
    state: Status;
    statusEffects: StatusEffect[];
    time: number;
};

class GrandmaError extends Error {}

const diff = (a: number, b: number) => Math.abs(a - b);

export class Grandma {
    private _state: Status = {
        hp: {
            value: 10,
            optimal: 10,
            min: 0,
            max: 10,
            key: 'hp',
            color: 'red',
        },
        happiness: {
            value: 8,
            optimal: 10,
            min: 0,
            max: 10,
            key: 'happiness',
            color: 'green',
        },
        sickness: {
            value: 0,
            optimal: 0,
            min: 0,
            max: 3,
            key: 'sickness',
            color: 'blue',
        },
        bloodPressure: {
            value: 5,
            optimal: 5,
            min: 0,
            max: 10,
            key: 'bloodPressure',
            color: 'purple',
        },
        /*sleepiness: {
            value: 0,
            optimal: 0,
            min: 0,
            max: 3,
            key: 'sleepiness',
            color: 'yellow',
        },
        isAsleep: {
            value: false,
            optimal: false,
            min: false,
            max: true,
            key: 'isAsleep',
            color: 'purple',
        },*/
    };
    private _statusEffects: Map<string, StatusEffect> = new Map();
    private _time = 0;
    private _schedule: Schedule|null = null;
    private _diary: Diary = [];

    simulate(schedule: Schedule, statusEffects: StatusEffect[] = []) {
        this._time = 0;
        this._schedule = schedule;
        //this._diary = {};
        this._statusEffects = new Map();
    }

    get time() {
        return this._time;
    }

    get diary() {
        return this._diary;
    }

    get state() {
        return this._state;
    }

    get score() {
        return Object.values(this._state).reduce((acc, value) => acc + (typeof value.value === 'number' ? diff(value.optimal, value.value) : 0), 0);
    }

    *tick(): Generator<GrandmaTickResult, void, unknown> {
        const page: DiaryPage = {
            day: `Day ${this._diary.length + 1}`,
            entries: [],
        };

        if (this._schedule === null) {
            throw new GrandmaError('Grammer has no schedule');
        }

        const lastTime = this._schedule[this._schedule.length - 1].time;

        while (this._time <= lastTime) {
            const schedule = this._schedule;
            const entry: DiaryEntry = {
                time: this._time,
                state: this._state,
                appliedEffects: [],
                tickEffects: [],
            };

            // cleanup status effects
            for (const [ key, effect ] of this._statusEffects) {
                if (this._time - effect.startTime >= effect.duration) {
                    const effect = this._statusEffects.get(key);
                    effect?.cleanup?.(effect, this._time, this._statusEffects, this._state, schedule);
                    this._statusEffects.delete(key);
                }
            }

            // add scheduled status effects
            schedule[this._time].effects.forEach(effect => {
                entry.appliedEffects.push(effect);
                if (Object.hasOwn(effect, 'apply') && !!effect.apply) {
                    this._state = effect.apply(effect, this._time, this._statusEffects, this._state, schedule);
                } else {
                    this._statusEffects.set(effect.key, { ...effect, startTime: this._time });
                }
            });

            // apply status effects
            this._statusEffects.forEach(effect => {
                entry.tickEffects.push(effect);
                const newState = effect.onTick(effect, this._time, this._statusEffects, this._state, schedule);
                this._state = newState;
            });


            this._state = Object.fromEntries(Object.entries(this._state).map(([ key, value ]) => ([
                key,
                {
                    ...value,
                    value: typeof value.value === 'number' ? Math.max(value.min, Math.min(value.max, value.value)) : value.value,
                },
            ]))) as Status;

            entry.state = this._state;
            page.entries.push(entry);

            if (this._time === lastTime - 1) {
                this._diary.push(page);
            }

            if (this._state.hp.value <= 0) {
                throw new GrandmaError('Grandma died');
            }

            yield {
                time: this._time,
                state: this._state,
                statusEffects: Array.from(this._statusEffects.values()),
            };
            this._time++;
        }
    }
}
